import React, { useEffect, useRef } from 'react';
import WebGLFluidEnhanced from 'webgl-fluid-enhanced';

/**
 * Cursor-driven fluid swirls (Navier-Stokes via webgl-fluid-enhanced, MIT).
 * Real fluid advection is what produces the vortex "mushroom" roll-up at the
 * end of a stroke and the thinning, graceful tail — tuned dim and slow here,
 * in the brand's green range.
 *
 * The library listens on its own canvas, which sits behind the page content,
 * so window pointer moves are forwarded to it as synthetic events (dispatch
 * ignores pointer-events CSS). Honors prefers-reduced-motion.
 */
const PALETTE = ['#b6ce3b', '#d3e662', '#9ab72e', '#7fa32c'];

const SwirlCanvas = ({ className }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    container.replaceChildren(); // drop any stale canvas (StrictMode remount)

    const sim = new WebGLFluidEnhanced(container);
    sim.setConfig({
      simResolution: 128,
      dyeResolution: 1024,
      densityDissipation: 1.3,
      velocityDissipation: 0.3,
      pressure: 0.8,
      pressureIterations: 20,
      curl: 24,
      splatRadius: 0.12,
      splatForce: 4200,
      shading: true,
      colorful: false,
      colorPalette: PALETTE,
      hover: true,
      transparent: true,
      brightness: 0.55,
      bloom: true,
      bloomIntensity: 0.3,
      bloomThreshold: 0.35,
      sunrays: false,
    });
    sim.start();

    const canvas = container.querySelector('canvas');

    const forward = (event) => {
      if (!canvas) return;
      canvas.dispatchEvent(new MouseEvent('mousemove', {
        clientX: event.clientX,
        clientY: event.clientY,
      }));
    };
    window.addEventListener('pointermove', forward, { passive: true });

    // A few soft splats on load, then the fluid is purely cursor-driven
    // (matching the reference site's behavior).
    const initialTimer = setTimeout(() => sim.multipleSplats(3), 400);

    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('pointermove', forward);
      sim.stop();
      container.replaceChildren();
    };
  }, []);

  // The library overwrites the container's inline `position` with
  // `relative`, so it must not be the absolutely-positioned layer itself:
  // give it explicit 100% dimensions inside the positioned wrapper.
  return (
    <div className={className} aria-hidden="true">
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default SwirlCanvas;
